import { Chip, Typography } from "@mui/material";
import helpers from "../hooks/HelperFunctions";
import { CampaignLink } from "./campaignLink";

const TEXT_BASE_STYLE = { fontSize: 12 };

export const getPocSummaryColumns = () => [
    {
        field: "pocName",
        headerName: "POC",
        width: 200,
        renderCell: ({ row }) => <Typography sx={TEXT_BASE_STYLE}>{row?.pocName || "Unknown POC"}</Typography>,
    },
    {
        field: "uniqueBankAccountCount",
        headerName: "Unique Bank Accounts",
        width: 190,
        type: "number",
    },
    {
        field: "duplicatedBankAccountCount",
        headerName: "Duplicated Bank Accounts",
        width: 210,
        type: "number",
        renderCell: ({ row }) => {
            const count = row?.duplicatedBankAccountCount || 0;
            return (
                <Chip
                    size="small"
                    label={count}
                    sx={{
                        fontWeight: 600,
                        backgroundColor: count > 0 ? "rgba(220,53,69,0.1)" : "rgba(25,135,84,0.1)",
                        color: count > 0 ? "#DC3545" : "#198754",
                    }}
                />
            );
        },
    },
];

// IFSC/account number are shown in the drill-down grid below, never CSV-exported
// or shown in full elsewhere — masking the account number here is a minimal,
// no-cost precaution against shoulder-surfing a shared screen.
const maskAccountNo = (accountNo) => {
    if (!accountNo) return "-";
    const str = String(accountNo);
    return str.length <= 4 ? str : `••••${str.slice(-4)}`;
};

// First drill-down level for a POC row in getPocSummaryColumns() above — one
// row per bank account THAT POC personally reused across 2+ of their own
// payment links (not flattened to individual links yet, since a single POC
// can have hundreds of these on real data — see getDuplicateGroupLinksColumns
// below for the per-payment level, entered by clicking one of these rows).
// No "POCs involved" column here — every row is already scoped to one POC's
// own reuse, not accounts merely shared with other POCs.
export const getDuplicateGroupSummaryColumns = () => [
    {
        field: "bankAccount",
        headerName: "Bank Account",
        width: 190,
        renderCell: ({ row }) => (
            <Typography sx={TEXT_BASE_STYLE}>
                {row?.ifscCode} · {maskAccountNo(row?.accountNo)}
            </Typography>
        ),
    },
    {
        field: "linkCount",
        headerName: "Payments",
        width: 130,
        type: "number",
    },
    {
        field: "distinctCampaignCount",
        headerName: "Campaigns",
        width: 120,
        type: "number",
    },
    {
        field: "totalPayableAmount",
        headerName: "Total Paid",
        width: 140,
        type: "number",
        renderCell: ({ value }) => (
            <Typography sx={{ ...TEXT_BASE_STYLE, fontWeight: 600 }}>{helpers.formatNumber(value, true)}</Typography>
        ),
    },
];

// Second drill-down level — every individual payment on ONE bank-account group
// selected from getDuplicateGroupSummaryColumns above. No POC column: every row
// here is already that same one POC's own payment (self-reuse, not sharing).
export const getDuplicateGroupLinksColumns = () => [
    {
        field: "campaignName",
        headerName: "Campaign",
        width: 190,
        renderCell: ({ row }) => <CampaignLink id={row?.campaignId} name={row?.campaignName} />,
    },
    {
        field: "companyName",
        headerName: "Company",
        width: 170,
        renderCell: ({ row }) => <Typography sx={TEXT_BASE_STYLE}>{row?.companyName || "-"}</Typography>,
    },
    {
        field: "accountName",
        headerName: "Account Name",
        width: 170,
        renderCell: ({ row }) => <Typography sx={TEXT_BASE_STYLE}>{row?.accountName || "-"}</Typography>,
    },
    {
        field: "pancard",
        headerName: "PAN",
        width: 120,
        renderCell: ({ row }) => <Typography sx={TEXT_BASE_STYLE}>{row?.pancard || "-"}</Typography>,
    },
    {
        field: "payableAmount",
        headerName: "Payable Amount",
        width: 140,
        type: "number",
        renderCell: ({ value }) => (
            <Typography sx={TEXT_BASE_STYLE}>{helpers.formatNumber(value, true)}</Typography>
        ),
    },
];
